/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/

import type { ICommonGridHeader } from '@broadleaf/admin-components/dist/types/common';
import messages from './InvoicesGridUtils.messages';

export const getDefaultInvoiceHeaders = (
  formatMessage
): Array<ICommonGridHeader> => {
  const statusOptions = getInvoiceStatusOptions(formatMessage);
  return [
    {
      attributes: {
        colSpan: 1,
        dateFormat: 'L'
      },
      classifier: 'Field',
      label: formatMessage(messages.headers.submitDate),
      name: 'submitDate',
      type: 'DATE',
      sortable: true,
      width: '110px',
      order: 100
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.orderNumber),
      name: 'orderNumber',
      type: 'STRING',
      sortable: true,
      width: '115px',
      order: 200
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.invoiceStatus),
      name: 'invoiceStatus',
      type: 'RAW_ENUM',
      sortable: true,
      options: statusOptions,
      width: '150px',
      order: 300
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.billToOrg),
      name: 'invoiceBillToOrganization',
      type: 'STRING',
      sortable: true,
      width: '170px',
      order: 400
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.invoiceTotal),
      name: 'invoiceTotal',
      type: 'MONEY',
      align: 'right',
      sortable: true,
      width: '110px',
      order: 500
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.revMedCreditAmount),
      name: 'invoiceRevMedCreditAmount',
      type: 'MONEY',
      align: 'right',
      sortable: true,
      width: '120px',
      order: 600
    },
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.invoiceBalance),
      name: 'invoiceBalance',
      type: 'MONEY',
      align: 'right',
      sortable: true,
      width: '120px',
      order: 700
    },
    {
      // place-holder for actions
      attributes: {
        // Want to set `sortable` flag to false as this is not data that needs to be sortable.
        sortable: false
      },
      classifier: 'Field',
      label: 'Actions',
      name: '',
      width: '70px',
      type: 'ROW_ACTIONS',
      order: 800
    }
  ];
};

export function getInvoiceStatusOptions(formatMessage) {
  const restingStatuses = [
    {
      color: 'primary',
      label: formatMessage(messages.statusOptions.resting.submitted),
      value: 'SUBMITTED'
    },
    {
      color: 'primary',
      label: formatMessage(messages.statusOptions.resting.invoicePaid),
      value: 'INVOICE_PAID'
    },
    {
      color: 'primary',
      label: formatMessage(messages.statusOptions.resting.reimbursed),
      value: 'REIMBURSED'
    },
    {
      color: 'danger',
      label: formatMessage(messages.statusOptions.resting.rejected),
      value: 'REJECTED',
      reason: {
        sourceField: 'invoiceStatusReason',
        tooltip: true
      }
    },
    {
      color: 'danger',
      label: formatMessage(messages.statusOptions.resting.disputed),
      value: 'DISPUTED',
      reason: {
        sourceField: 'invoiceStatusReason',
        tooltip: true
      }
    }
  ];

  const activeStatuses = [
    {
      color: 'info',
      label: formatMessage(messages.statusOptions.active.orderProcessing),
      value: 'ORDER_PROCESSING'
    },
    {
      color: 'info',
      label: formatMessage(messages.statusOptions.active.creditPending),
      value: 'CREDIT_PENDING'
    },
    {
      color: 'info',
      label: formatMessage(messages.statusOptions.active.readyToSubmit),
      value: 'READY_TO_SUBMIT'
    },
    {
      color: 'info',
      label: formatMessage(messages.statusOptions.active.openRevMedInvoice),
      value: 'OPEN_REVMED_INVOICE'
    }
  ];

  const errorStatuses = [
    {
      color: 'danger',
      label: formatMessage(messages.statusOptions.error.holdForReview),
      value: 'HOLD_FOR_REVIEW',
      reason: {
        sourceField: 'invoiceStatusReason',
        tooltip: true
      }
    },
    {
      color: 'danger',
      label: formatMessage(messages.statusOptions.error.requiresPo),
      value: 'REQUIRES_PO'
    }
  ];
  return [...restingStatuses, ...activeStatuses, ...errorStatuses];
}

export function getPaymentTypes(formatMessage) {
  return [
    {
      color: 'primary',
      label: formatMessage(messages.paymentTypes.invoiceCredit),
      value: 'INVOICE_CREDIT',
      reason: {
        sourceField: 'invoiceCreditProvider',
        tooltip: true
      }
    },
    {
      color: 'primary',
      label: formatMessage(messages.paymentTypes.storeCredit),
      value: 'STORE_CREDIT',
      reason: {
        sourceField: 'invoiceCreditProvider',
        tooltip: true
      }
    },
    {
      color: 'info',
      label: formatMessage(messages.paymentTypes.wire),
      value: 'WIRE'
    },
    {
      color: 'info',
      label: formatMessage(messages.paymentTypes.paypal),
      value: 'PAYPAL'
    },
    {
      color: 'info',
      label: formatMessage(messages.paymentTypes.other),
      value: 'OTHER'
    },
    {
      color: 'info',
      label: formatMessage(messages.paymentTypes.creditCard),
      value: 'CREDIT_CARD'
    }
  ];
}
